/**
 * kwKontrol.js — the free-kW check of an electricity request (backlog #455 backend, #456 / #458 frontend, 2026-10-17).
 *
 * The backend puts `kwKontrol` on the Elektrik row of every Teknik read (GET /stands/talepler/{id}/teknik, the request lists):
 *   { serbestKw, istenenKw, asanKw, odemeGerekli, standAlaniM2, alanKaynagi: 'KROKI', uyariKodu, uyari, odeme: { url, host, etiket } }
 * `serbestKw` is the kW the fair gives free for this stand (the fair's FLAT / BLOCK / BANDS rule at the stand's drawn area); null = nothing to
 * judge against (no rule, or no drawn area where the rule needs one): then there is no warning. `odeme` is there only when the saved request is above
 * the allowance AND the organizer registered a payment link. The warning is NEVER a refusal: the request is saved whatever it asks for.
 *
 * Pure on purpose (no React, no i18n): the form, the organizer's rows and the tests all use it.
 */

/** A finite number from a form value ("12", 12, "12,5") or null. */
export function kwSayisi(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).trim().replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/**
 * What a requested kW means against the check the backend sent: { serbest, istenen, asan, ucretli }. `istenen` is what is being typed (or the saved value);
 * `ucretli` is true only when both numbers exist and the request is ABOVE the allowance (the allowance itself is free). Never throws.
 */
export function kwAsimi(kwKontrol, istenenKw) {
  const serbest = kwSayisi(kwKontrol?.serbestKw);
  const istenen = kwSayisi(istenenKw);
  if (serbest === null || istenen === null || istenen <= serbest) {
    return { serbest, istenen, asan: 0, ucretli: false };
  }
  // 2 decimals like the backend's DECIMAL(10,2), without floating point noise (20 - 15.1 must read 4.9)
  const asan = Math.round((istenen - serbest) * 100) / 100;
  return { serbest, istenen, asan, ucretli: true };
}

/**
 * The payment link as it may be shown: { url, host, etiket } only when the URL is https and has a host, else null. The backend already refuses anything
 * else when the link is saved, but the link goes to every exhibitor, so the page checks again before it draws a button (javascript:, data:, http: never).
 */
export function guvenliOdemeBaglantisi(odeme) {
  const ham = odeme?.url;
  if (typeof ham !== 'string' || !/^https:\/\//i.test(ham.trim())) return null;
  let url;
  try {
    url = new URL(ham.trim());
  } catch {
    return null;
  }
  // the backend's own rules, again: a domain name (a dot; not localhost, not an IP address), no user name or password
  const host = url.hostname.toLowerCase();
  if (url.protocol !== 'https:' || !host || !host.includes('.') || host.startsWith('[') || /^[0-9.]+$/.test(host) || url.username || url.password) return null;
  return { url: url.href, host, etiket: odeme.etiket || null };
}

/** The Elektrik row of a list of Teknik rows, or null. */
export function elektrikSatiri(teknik) {
  return (teknik ?? []).find((t) => t?.categoryKey === 'elektrik') ?? null;
}

/** A kW number for a sentence: at most 2 decimals, in the page's number format. */
export function kwYazi(n, yerel) {
  const v = kwSayisi(n);
  if (v === null) return '';
  return v.toLocaleString(yerel || undefined, { maximumFractionDigits: 2 });
}
